import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { X, Clock, Flame, CheckCircle2, Loader2, ArrowRight, AlertTriangle, Share2, Copy } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

/*  THE 60-SECOND INTERROGATION — the first-visit micro-case.
    Three suspects. Three clues. Sixty seconds. One vote. Shareable verdict.
    Dismissible, remembered via localStorage for 7 days so returning
    visitors don't get re-offered.
*/

const DISMISS_KEY   = "wm_interrogation_dismissed_v1";
const PLAYED_KEY    = "wm_interrogation_played_v1";
const INTRO_SEEN_KEY = "wm_transmission_seen_v1";

const _recently = (key, hours = 7 * 24) => {
  try {
    const raw = parseInt(localStorage.getItem(key) || "0", 10);
    if (!raw) return false;
    return (Date.now() - raw) / 36e5 < hours;
  } catch { return false; }
};

export const Interrogation = () => {
  const [visible, setVisible] = useState(false);
  const [caseData, setCaseData] = useState(null);
  const [picked,  setPicked]  = useState("");
  const [left,    setLeft]    = useState(60);
  const [running, setRunning] = useState(false);
  const [verdict, setVerdict] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const openedRef = useRef(false);
  const timerRef  = useRef(null);

  // Mount after the Transmission intro clears + previously not dismissed
  useEffect(() => {
    if (window.location.pathname !== "/") return;
    if (_recently(DISMISS_KEY)) return;
    if (_recently(PLAYED_KEY, 20))  return;   // played in last 20h → skip
    let cancelled = false;
    const check = () => {
      if (cancelled) return;
      try {
        if (localStorage.getItem(INTRO_SEEN_KEY) === "1") {
          // Delay so Kairo opens first, THEN the interrogation offers
          setTimeout(() => { if (!cancelled) setVisible(true); }, 6500);
        } else {
          setTimeout(check, 500);
        }
      } catch { setVisible(true); }
    };
    check();
    return () => { cancelled = true; };
  }, []);

  // First open — fetch the case
  useEffect(() => {
    if (!visible || openedRef.current) return;
    openedRef.current = true;
    api.get("/interrogation/case")
      .then((r) => setCaseData(r.data.case))
      .catch(() => setVisible(false));
  }, [visible]);

  // Countdown tick
  useEffect(() => {
    if (!running) return;
    timerRef.current = setInterval(() => {
      setLeft((n) => {
        if (n <= 1) {
          clearInterval(timerRef.current);
          // Auto-submit with whatever is picked (or empty)
          submit(0);
          return 0;
        }
        return n - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running]);

  const start = () => {
    setLeft(60);
    setRunning(true);
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setVisible(false);
  };

  const submit = async (timeLeftOverride = null) => {
    if (submitting) return;
    setSubmitting(true);
    clearInterval(timerRef.current);
    setRunning(false);
    try {
      const { data } = await api.post("/interrogation/verdict", {
        guess_key:   picked || "none",
        time_left_s: timeLeftOverride ?? left,
      });
      setVerdict(data);
      try { localStorage.setItem(PLAYED_KEY, String(Date.now())); } catch {}
    } catch {
      setVerdict({ correct: false, score: 0, reveal: "Comms stuttered — the file closed.", global_accuracy: 0 });
    } finally {
      setSubmitting(false);
    }
  };

  if (!visible) return null;
  if (!caseData) return null;

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-end sm:items-center justify-center p-4 interrogation-root"
      role="dialog"
      aria-label="60-Second Interrogation"
      data-testid="interrogation-overlay"
    >
      <div className="relative w-full max-w-xl bg-[#0a0908] border border-[#8b0000]/50 shadow-2xl overflow-hidden rounded-lg">
        <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-[#8b0000] to-transparent" />

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#2a2525]">
          <div className="flex items-center gap-2">
            <Flame size={14} className="text-[#d4af37]" />
            <div className="text-[10px] tracking-[0.3em] text-[#d4af37]">60-SECOND INTERROGATION</div>
          </div>
          <button onClick={dismiss} className="text-[#5a5050] hover:text-[#d4af37]" data-testid="interrogation-dismiss">
            <X size={16} />
          </button>
        </div>

        {/* Verdict screen */}
        {verdict ? (
          <div className="p-6 sm:p-8" data-testid="interrogation-verdict">
            <div className="text-center mb-6">
              {verdict.correct ? (
                <>
                  <CheckCircle2 size={48} className="text-[#d4af37] mx-auto mb-3" />
                  <div className="font-display text-3xl text-[#f4f0ec] mb-1">You cracked it.</div>
                  <div className="text-sm text-[#d4af37]">Score: {verdict.score} · {verdict.time_bonus > 0 ? `+${verdict.time_bonus} time bonus` : ""}</div>
                </>
              ) : (
                <>
                  <AlertTriangle size={48} className="text-[#8b0000] mx-auto mb-3" />
                  <div className="font-display text-3xl text-[#f4f0ec] mb-1">Case closed. Wrong one.</div>
                  <div className="text-sm text-[#a39797]">The real one was <span className="text-[#d4af37]">{verdict.killer_name}</span>.</div>
                </>
              )}
            </div>

            <div className="card-noir p-4 mb-5">
              <div className="text-[10px] tracking-[0.3em] text-[#d4af37] mb-2">THE REVEAL</div>
              <div className="text-sm text-[#d4d0cc] leading-relaxed">{verdict.reveal}</div>
            </div>

            {verdict.global_plays_today > 0 && (
              <div className="text-center text-[10px] text-[#5a5050] mb-5">
                {verdict.global_correct_today} of {verdict.global_plays_today} detectives solved today's case ({verdict.global_accuracy}% accuracy)
                {verdict.detective_number ? ` · YOU ARE DETECTIVE #${verdict.detective_number}` : ""}
              </div>
            )}

            {/* Share-A-Verdict Card — one-tap native share + copy fallback */}
            {verdict.share_card_url && (
              <div className="card-noir p-4 mb-5" data-testid="interrogation-share-card">
                <div className="flex items-center gap-2 mb-3">
                  <Share2 size={13} className="text-[#d4af37]" />
                  <div className="text-[10px] tracking-[0.3em] text-[#d4af37]">YOUR SHAREABLE VERDICT CARD</div>
                </div>
                <div className="rounded-md overflow-hidden border border-[#2a2525] mb-3 bg-[#1a1515]">
                  <img
                    src={verdict.share_card_url}
                    alt="Your Interrogation verdict card"
                    className="w-full h-auto"
                    data-testid="interrogation-share-img"
                    loading="lazy"
                  />
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={async () => {
                      const title = verdict.correct
                        ? `I cracked today's WasntMe interrogation in ${60 - (verdict.time_left_s || 0)}s · score ${verdict.score}`
                        : `I just blew today's WasntMe interrogation. Think you can do better?`;
                      const shareUrl = `${window.location.origin}/?from=verdict`;
                      try {
                        if (navigator.share) {
                          await navigator.share({ title, text: title, url: shareUrl });
                        } else {
                          await navigator.clipboard.writeText(`${title}\n${shareUrl}`);
                          toast.success("Copied — paste it anywhere.");
                        }
                      } catch {}
                    }}
                    className="btn-primary text-xs"
                    data-testid="interrogation-share-native"
                  >
                    <Share2 size={12} /> Share my verdict
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(verdict.share_card_url);
                        toast.success("Card URL copied.");
                      } catch {}
                    }}
                    className="btn-ghost text-xs"
                    data-testid="interrogation-share-copy"
                  >
                    <Copy size={12} /> Copy image URL
                  </button>
                </div>
              </div>
            )}

            <div className="flex items-center justify-center gap-2 flex-wrap">
              <Link to="/packs" className="btn-primary text-sm" data-testid="interrogation-cta-packs">
                Run a case with your crew <ArrowRight size={13} />
              </Link>
              <button onClick={dismiss} className="btn-ghost text-xs" data-testid="interrogation-close">
                Close
              </button>
            </div>
          </div>
        ) : running ? (
          /* Playing screen */
          <div className="p-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="text-[10px] tracking-[0.3em] text-[#a39797]">CASE #{caseData.id.replace("_", " ").toUpperCase()}</div>
              <div className="flex items-center gap-1.5 text-[#d4af37]">
                <Clock size={13} />
                <span className="font-display text-xl tracking-wider">
                  00:{String(left).padStart(2, "0")}
                </span>
              </div>
            </div>
            <h2 className="font-display text-2xl text-[#f4f0ec] mb-1 leading-tight">{caseData.title}</h2>
            <p className="text-xs text-[#a39797] mb-4 leading-relaxed">{caseData.setting}</p>

            <div className="card-noir p-3 mb-4" data-testid="interrogation-clues">
              <div className="text-[9px] tracking-[0.3em] text-[#d4af37] mb-2">THREE CLUES</div>
              <ol className="space-y-1.5 text-[11px] text-[#d4d0cc] leading-relaxed list-decimal list-inside">
                {caseData.clues.map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ol>
            </div>

            <div className="text-[9px] tracking-[0.3em] text-[#d4af37] mb-2">WHO DID IT?</div>
            <div className="space-y-2 mb-4">
              {caseData.suspects.map((s) => (
                <button
                  key={s.key}
                  onClick={() => setPicked(s.key)}
                  className={`w-full text-left rounded-md border px-3 py-2.5 transition-colors ${
                    picked === s.key
                      ? "border-[#d4af37] bg-[#d4af37]/5"
                      : "border-[#2a2525] bg-[#1a1515] hover:border-[#8b0000]/60"
                  }`}
                  data-testid={`interrogation-suspect-${s.key}`}
                >
                  <div className="text-sm text-[#f4f0ec]">{picked === s.key ? "✓ " : ""}{s.name}</div>
                  <div className="text-[10px] text-[#a39797] mt-0.5">{s.blurb}</div>
                </button>
              ))}
            </div>

            <button
              onClick={() => submit()}
              disabled={!picked || submitting}
              className="btn-primary w-full py-3"
              data-testid="interrogation-accuse"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <AlertTriangle size={14} />}
              {submitting ? "Filing the report…" : "LOCK IN MY ACCUSATION"}
            </button>
          </div>
        ) : (
          /* Intro screen */
          <div className="p-6 sm:p-8 text-center" data-testid="interrogation-intro">
            <div className="text-[10px] tracking-[0.4em] text-[#8b0000] mb-3">◉ KAIRO IS WAITING</div>
            <h2 className="font-display text-3xl text-[#f4f0ec] mb-2 leading-tight">
              60 seconds.<br />Three suspects.<br /><span className="text-[#d4af37]">One liar.</span>
            </h2>
            <p className="text-xs text-[#a39797] max-w-sm mx-auto mb-5 leading-relaxed">
              Free. No signup. Pick who did it before the clock hits zero. You'll get a score, the reveal, and bragging rights.
            </p>
            <button
              onClick={start}
              className="btn-primary"
              data-testid="interrogation-start"
            >
              <Flame size={13} /> START THE CLOCK
            </button>
            <div className="text-[9px] text-[#5a5050] mt-4">Esc or ✕ to skip</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Interrogation;
